import { useCallback, useEffect, useState } from 'react';
import { Users, Plus, Edit2, Trash2, Shield, Settings, MoreVertical, Zap, Terminal } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { DropdownMenu } from '../../components/ui/DropdownMenu';
import { apiClient } from '../../lib/api-client';
import { useAuth } from '../../contexts/AuthContext';
import { CreateGroupDialog } from '../../components/admin/CreateGroupDialog';
import { ManageMembersDialog } from '../../components/admin/ManageMembersDialog';
import { PermissionEditor } from '../../components/admin/PermissionEditor';
import { RBACYamlEditor } from '../../components/admin/RBACYamlEditor';
import { RbacSchemaDialog } from '../../components/RbacSchemaDialog';
import { WorkflowPermissionsDialog } from '../../components/admin/WorkflowPermissionsDialog';
import { CliPermissionsDialog } from '../../components/admin/CliPermissionsDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../components/ui/Dialog';
import { InlineConfirmation } from '../../components/ui/InlineConfirmation';
import { Skeleton } from '../../components/ui/Skeleton';
import { usePermission } from '../../hooks/usePermission';
import { useActivityHeartbeat } from '../../hooks/useActivityHeartbeat';
import { useCacheEvents } from '../../hooks/useCacheEvents';
import { PageHeader } from '../../components/ui/PageHeader';

// RBAC Group from group-permissions.yaml
interface RBACGroup {
  groupName: string;
  description?: string;
  members: string[]; // Array of user emails
  portal_permissions: Record<string, any>;
  tenant_access: string[];
  permission_flags: Record<string, boolean>;
}

export function Groups() {
  useAuth();
  const { isAllowed: canView } = usePermission('user_groups', 'read');
  const { isAllowed: canCreateGroup } = usePermission('user_groups', 'create_group');
  const { isAllowed: canEditRBACConfig } = usePermission('user_groups', 'edit_rbac_config');
  const { isAllowed: canManageMembers } = usePermission('user_groups', 'manage_members');
  const { isAllowed: canEditPermissions } = usePermission('user_groups', 'edit_permissions');
  const { isAllowed: canDeleteGroup } = usePermission('user_groups', 'delete');
  const [groups, setGroups] = useState<RBACGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isPermDialogOpen, setIsPermDialogOpen] = useState(false);
  const [isMembersDialogOpen, setIsMembersDialogOpen] = useState(false);
  const [isYamlEditorOpen, setIsYamlEditorOpen] = useState(false);
  const [isRbacSchemaOpen, setIsRbacSchemaOpen] = useState(false);
  const [isWorkflowPermsOpen, setIsWorkflowPermsOpen] = useState(false);
  const [isCliPermsOpen, setIsCliPermsOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<RBACGroup | null>(null);
  const [confirmingDeleteGroup, setConfirmingDeleteGroup] = useState<string | null>(null);
  
  // For new group creation flow
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  
  // Editor state
  const [editorPermissions, setEditorPermissions] = useState<any>({});
  const [editorFlags, setEditorFlags] = useState<any>({});
  const [editorTenantAccess, setEditorTenantAccess] = useState<string[]>([]);
  const [availableTenants, setAvailableTenants] = useState<string[]>([]);

  useActivityHeartbeat('cache:rbac', canView);
  
  useEffect(() => {
    void fetchGroups();
    void fetchTenants();
  }, []);
  
  const fetchGroups = useCallback(async () => {
    try {
      setIsLoading(true);
      const response: any = await apiClient.get('/api/admin/yaml-groups');
      setGroups((response as any)?.groups || []);
    } catch (err: any) {
      console.error('Failed to fetch groups:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh only when backend RBAC cache version changes.
  useCacheEvents({
    enabled: canView,
    cacheNames: ['rbac'],
    onEvent: () => {
      void fetchGroups();
    },
  });
  
  const handleCreateGroupSubmit = async (name: string, description: string) => {
    // Store for permission editor
    setNewGroupName(name);
    setNewGroupDescription(description);
    setSelectedGroup(null);
    setEditorPermissions({});
    setEditorFlags({});
    setEditorTenantAccess([]);
    setIsCreateDialogOpen(false);
    setIsPermDialogOpen(true);
  };
  
  const handleSavePermissions = async (permissions: any, tenantAccess: string[], flags: any) => {
    try {
      if (selectedGroup) {
        // Editing existing group - update both permissions and description
        await apiClient.patch(`/api/admin/yaml-groups/${selectedGroup.groupName}/permissions`, {
          description: newGroupDescription,
          portal_permissions: permissions,
          tenant_access: tenantAccess,
          permission_flags: flags
        });
      } else {
        // Creating new group
        await apiClient.post('/api/admin/yaml-groups', {
          groupName: newGroupName,
          description: newGroupDescription,
          portal_permissions: permissions,
          tenant_access: tenantAccess,
          permission_flags: flags
        });
        setNewGroupName('');
        setNewGroupDescription('');
      }
      setIsPermDialogOpen(false);
      setNewGroupDescription(''); // Clear after edit too
      fetchGroups();
    } catch (error: any) {
      console.error('Failed to save group:', error);
      alert('Failed to save group: ' + error.message);
    }
  };
  
  const handleEditPermissions = (group: RBACGroup) => {
    setSelectedGroup(group);
    // YAML groups have permissions at root level
    const perms = group.portal_permissions || {};
    const flags = group.permission_flags || {};
    const tenants = group.tenant_access || [];
    
    setEditorPermissions(typeof perms === 'string' ? JSON.parse(perms) : perms);
    setEditorFlags(typeof flags === 'string' ? JSON.parse(flags) : flags);
    setEditorTenantAccess(Array.isArray(tenants) ? tenants : []);
    setNewGroupDescription(group.description || '');
    setIsPermDialogOpen(true);
  };
  
  const handleManageMembers = (group: RBACGroup) => {
    setSelectedGroup(group);
    setIsMembersDialogOpen(true);
  };

  const handleWorkflowPermissions = (group: RBACGroup) => {
    setSelectedGroup(group);
    setIsWorkflowPermsOpen(true);
  };

  const handleCliPermissions = (group: RBACGroup) => {
    setSelectedGroup(group);
    setIsCliPermsOpen(true);
  };
  
  const handleDeleteGroup = async (group: RBACGroup) => {
    try {
      await apiClient.delete(`/api/admin/yaml-groups/${group.groupName}`);
      fetchGroups();
    } catch (error: any) {
      console.error('Failed to delete group:', error);
      alert('Failed to delete group: ' + error.message);
    } finally {
      setConfirmingDeleteGroup(null);
    }
  };
  
  const fetchTenants = async () => {
    try {
      const response = await apiClient.get<{ tenants: string[] }>('/api/admin/yaml-groups/tenants');
      setAvailableTenants((response.tenants || []).map(t => String(t).toLowerCase()).filter(Boolean).sort());
    } catch {
      // keep empty; editor will still work with manual YAML if needed
      setAvailableTenants([]);
    }
  };
  
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton variant="circle" className="w-10 h-10" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-36" />
              <Skeleton className="h-4 w-64" />
            </div>
          </div>
          <Skeleton className="h-9 w-28" />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="w-full min-w-0 overflow-hidden">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Skeleton variant="circle" className="w-9 h-9" />
                    <div className="flex-1 min-w-0">
                      <Skeleton className="h-5 w-40" />
                      <Skeleton className="h-4 w-56 mt-2" />
                    </div>
                  </div>
                  <Skeleton className="h-8 w-8" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Skeleton className="h-4 w-16 mb-2" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <div className="flex gap-2">
                    <Skeleton className="h-6 w-28 rounded-full" />
                    <Skeleton className="h-6 w-24 rounded-full" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <p className="text-red-600 dark:text-red-400">Failed to load groups: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <PageHeader
        icon={Users}
        iconColor="text-indigo-600 dark:text-indigo-400"
        iconBgColor="bg-indigo-100 dark:bg-indigo-900/30"
        description="Manage user groups and their permissions"
        count={groups.length}
        countLabel="groups"
        countColor="text-indigo-600 dark:text-indigo-400"
        actions={
          <div className="w-full sm:w-auto sm:h-full flex items-stretch">
            <DropdownMenu
              className="w-full h-full"
              trigger={
                <Button variant="outline" size="sm" className="w-full h-full text-sm py-2">
                  <Settings className="w-4 h-4 mr-2" />
                  Actions
                </Button>
              }
              items={[
                canCreateGroup
                  ? {
                      label: 'Create Group',
                      icon: <Plus className="w-4 h-4" />,
                      onClick: () => setIsCreateDialogOpen(true),
                    }
                  : null,
                canEditRBACConfig
                  ? {
                      label: 'Edit RBAC Config',
                      icon: <Edit2 className="w-4 h-4" />,
                      onClick: () => setIsYamlEditorOpen(true),
                    }
                  : null,
                canView
                  ? {
                      label: 'View RBAC Schema',
                      icon: <Shield className="w-4 h-4" />,
                      onClick: () => setIsRbacSchemaOpen(true),
                    }
                  : null,
              ].filter(Boolean) as any[]}
              align="end"
            />
          </div>
        }
      />
      
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {groups.map((group) => {
          const extensions = (group as any)?.['urn:ietf:params:scim:schemas:extension:enterprise:2.0:Group'] as any | undefined;
          const permsRaw = extensions?.portal_permissions ?? (group as any)?.portal_permissions;
          const flagsRaw = extensions?.permission_flags ?? (group as any)?.permission_flags;
          const tenantAccess = extensions?.tenant_access ?? (group as any)?.tenant_access;

          const permissions = permsRaw
            ? (typeof permsRaw === 'string' ? JSON.parse(permsRaw) : permsRaw)
            : {};
          const flags = flagsRaw
            ? (typeof flagsRaw === 'string' ? JSON.parse(flagsRaw) : flagsRaw)
            : {};
          
          return (
            <Card key={group.groupName}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                      <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <CardTitle>{group.groupName}</CardTitle>
                      {group.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {group.description}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {(() => {
                    const items = [
                      canManageMembers
                        ? {
                            label: 'Manage Members',
                            icon: <Users className="w-4 h-4" />,
                            onClick: () => handleManageMembers(group),
                          }
                        : null,
                      canEditPermissions
                        ? {
                            label: 'Edit Permissions',
                            icon: <Edit2 className="w-4 h-4" />,
                            onClick: () => handleEditPermissions(group),
                          }
                        : null,
                      canEditPermissions
                        ? {
                            label: 'Workflow Permissions',
                            icon: <Zap className="w-4 h-4" />,
                            onClick: () => handleWorkflowPermissions(group),
                          }
                        : null,
                      canEditPermissions
                        ? {
                            label: 'CLI Permissions',
                            icon: <Terminal className="w-4 h-4" />,
                            onClick: () => handleCliPermissions(group),
                          }
                        : null,
                      canDeleteGroup
                        ? {
                            label: 'Delete Group',
                            icon: <Trash2 className="w-4 h-4" />,
                            onClick: () => setConfirmingDeleteGroup(group.groupName),
                            className: 'text-red-600 dark:text-red-400',
                          }
                        : null,
                    ].filter(Boolean) as any[];

                    if (items.length === 0) return null;

                    return (
                      <DropdownMenu
                        trigger={
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        }
                        items={items}
                        align="end"
                      />
                    );
                  })()}
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-4">
                  {confirmingDeleteGroup === group.groupName && (
                    <InlineConfirmation
                      message={`Delete group "${group.groupName}"? This cannot be undone.`}
                      confirmText="Delete"
                      onConfirm={() => handleDeleteGroup(group)}
                      onCancel={() => setConfirmingDeleteGroup(null)}
                    />
                  )}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Members
                    </h4>
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <Users className="w-4 h-4" />
                      {group.members?.length || 0} members
                    </div>
                  </div>
                  
                  {flags.is_admin && (
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                      System Administrator
                    </div>
                  )}
                  
                  {flags.all_pages_all_options && !flags.is_admin && (
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                      Full Admin Access
                    </div>
                  )}
                  
                  {flags.all_tenants && !flags.all_pages_all_options && !flags.is_admin && (
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      All Tenants Access
                    </div>
                  )}
                  
                  {Array.isArray(tenantAccess) && tenantAccess.length > 0 && !flags.all_tenants && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tenant Access
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {tenantAccess.map((tenant: string) => (
                          <span 
                            key={tenant}
                            className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                          >
                            {tenant}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {Object.keys(permissions).length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Permissions
                      </h4>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {Object.keys(permissions).length} page(s) configured
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
        
        {groups.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <Shield className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No groups yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Create your first group to get started
              </p>
              {canCreateGroup && (
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Group
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Dialogs */}
      <CreateGroupDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateGroupSubmit}
      />
      
      <Dialog open={isPermDialogOpen} onOpenChange={(open) => !open && setIsPermDialogOpen(false)}>
        <DialogContent maxWidth="4xl">
          <DialogHeader>
            <DialogTitle>
              {selectedGroup ? `Edit Permissions: ${selectedGroup.groupName}` : 'Set Group Permissions'}
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={newGroupDescription}
                  onChange={(e) => setNewGroupDescription(e.target.value)}
                  placeholder="Enter group description"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              <PermissionEditor
                permissions={editorPermissions}
                flags={editorFlags}
                tenantAccess={editorTenantAccess}
                availableTenants={availableTenants}
                onChange={(data) => {
                  setEditorPermissions(data.permissions);
                  setEditorFlags(data.flags);
                  setEditorTenantAccess(data.tenantAccess);
                }}
              />
            </div>
          </DialogBody>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsPermDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleSavePermissions(editorPermissions, editorTenantAccess, editorFlags)}>
              Save Permissions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {selectedGroup && (
        <ManageMembersDialog
          isOpen={isMembersDialogOpen}
          groupId={selectedGroup.groupName}
          groupName={selectedGroup.groupName}
          onClose={() => setIsMembersDialogOpen(false)}
          onUpdate={fetchGroups}
        />
      )}
      
      <RBACYamlEditor
        isOpen={isYamlEditorOpen}
        onClose={() => setIsYamlEditorOpen(false)}
        onSuccess={fetchGroups}
      />
      
      <RbacSchemaDialog
        open={isRbacSchemaOpen}
        onClose={() => setIsRbacSchemaOpen(false)}
      />

      {selectedGroup && (
        <WorkflowPermissionsDialog
          open={isWorkflowPermsOpen}
          onClose={() => setIsWorkflowPermsOpen(false)}
          groupName={selectedGroup.groupName}
          onSaved={fetchGroups}
        />
      )}

      {selectedGroup && (
        <CliPermissionsDialog
          open={isCliPermsOpen}
          onClose={() => setIsCliPermsOpen(false)}
          groupName={selectedGroup.groupName}
          onSaved={fetchGroups}
        />
      )}
    </div>
  );
}
